import PropTypes from 'prop-types';

import { useNavigate } from 'react-router';

import { ClassNames, css, legacyTheme, styled } from '@prose-ui/legacy';

import { Button } from 'Components/LegacyButton';
import { ProgressTracker } from 'Components/ProgressTracker';
import { Typography } from 'Components/Typography';

import { ResponsiveImageProps } from 'PropTypes/consultationWelcomeProps';

import { LegacyBaseScene as BaseScene } from 'Scenes/LegacyBaseScene';

import contentDI from 'assets/content/consultation/hero';

import useQueryParams from 'utils/useQueryParams';
import useResponsiveVariant from 'utils/useResponsiveVariant';

import { useConsultationCategory } from '../hooks/useConsultationCategory';
import { useConsultationSubsetCategory } from '../hooks/useConsultationSubsetCategory';

const contentOverrideStyles = css`
  align-items: flex-end;
  padding-top: ${legacyTheme.spacing.s16};

  ${legacyTheme.breakpoints.match('md')} {
    align-items: inherit;
    justify-content: flex-start;
  }
`;

const ImageContainer = styled.div`
  position: absolute;
  top: 0px;
  right: 0px;
  bottom: 0px;
  left: 0px;

  background-image: ${({ image }) => `url(${image.src})`};
  background-position: center;
  background-size: cover;

  ${legacyTheme.breakpoints.match('md')} {
    bottom: -15px;
  }
`;

const BlockContent = styled.div`
  position: relative;

  width: 430px;

  margin-right: 64px;

  ${legacyTheme.breakpoints.match('md')} {
    margin-right: 0;
    width: auto;
    padding: 32px 18px 0;
  }
`;

const StyledButton = styled(Button)`
  display: block;
  width: 327px;
  margin-left: auto;
  margin-right: auto;
`;

const Description = styled(Typography)`
  margin-top: ${legacyTheme.spacing.s48};
`;

const StoredAnswersWrapper = styled.div`
  margin-top: 50px;
`;

const ConsultationHero = ({
  content,
  hasStoredAnswers,
  isReturning,
  onClick,
  onResetStoredData,
  progressBarIndex,
  routeParams,
  showSignin,
  userFirstName,
  variant,
}) => {
  const consultationCategory = useConsultationCategory();
  const consultationSubsetCategory = useConsultationSubsetCategory();
  const { responsiveVariant } = useResponsiveVariant();
  const navigate = useNavigate();
  const query = useQueryParams();
  const next =
    query.get('from') === 'supplements-page'
      ? '/consultation/haircare?questionSet=supplements'
      : '';

  const { signin, returningProspect } = content;
  const {
    title,
    subtitle,
    cta,
    showProgressTracker = true,
    image,
    cta2 = '',
    subtitle2 = '',
  } = {
    ...variant,
    ...(hasStoredAnswers || isReturning ? returningProspect : {}),
  };
  const newTitle = isReturning ? title.replace('!', ` ${userFirstName}!`) : title;
  const imageSrc = image[responsiveVariant];

  return (
    <ClassNames>
      {({ css }) => (
        <BaseScene
          background="beige"
          classes={{ content: css(contentOverrideStyles) }}
          header
          relativePosition
        >
          {showProgressTracker && (
            <ProgressTracker
              consultationCategory={consultationCategory}
              consultationSubsetCategory={consultationSubsetCategory}
              questionIndex={progressBarIndex}
              routeParams={routeParams}
            />
          )}

          <ImageContainer image={imageSrc} />

          <BlockContent>
            <Typography
              align="center"
              color="noir"
              data-testid="consultation-hero-title"
              paragraph
              variant="h1r"
            >
              {newTitle}
            </Typography>

            <Typography align="center" color="noir" paragraph variant="p2">
              {subtitle}
            </Typography>

            <StyledButton
              data-testid="consultation-hero-cta"
              onClick={onClick}
              type="button"
              variant="vert"
            >
              {cta}
            </StyledButton>

            {showSignin && (
              <>
                <Description align="center" color="noir" paragraph variant="p2">
                  {signin.description}
                </Description>

                <StyledButton
                  data-click="sign-in"
                  data-from="consultation-intro"
                  onClick={() => navigate(`${signin.url}${next ? `?next=${next}` : ''}`)}
                  type="button"
                  variant="vertOutline"
                >
                  {signin.cta}
                </StyledButton>
              </>
            )}

            {!isReturning && hasStoredAnswers && (
              <StoredAnswersWrapper>
                <Typography align="center" color="noir" paragraph variant="p2">
                  {subtitle2}
                </Typography>

                <StyledButton onClick={onResetStoredData} type="button" variant="vertOutline">
                  {cta2}
                </StyledButton>
              </StoredAnswersWrapper>
            )}
          </BlockContent>
        </BaseScene>
      )}
    </ClassNames>
  );
};

ConsultationHero.propTypes = {
  content: PropTypes.shape({
    signin: PropTypes.shape({
      description: PropTypes.string,
      url: PropTypes.string,
      cta: PropTypes.string,
    }),
    returningProspect: PropTypes.shape({
      title: PropTypes.string,
      subtitle: PropTypes.string,
      subtitle2: PropTypes.string,
      cta: PropTypes.string,
      cta2: PropTypes.string,
      showProgressTracker: PropTypes.bool,
    }),
  }),
  hasStoredAnswers: PropTypes.bool.isRequired,
  isReturning: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  onResetStoredData: PropTypes.func.isRequired,
  progressBarIndex: PropTypes.number.isRequired,
  routeParams: PropTypes.shape({}),
  showSignin: PropTypes.bool.isRequired,
  userFirstName: PropTypes.string,
  variant: PropTypes.shape({
    title: PropTypes.node,
    subtitle: PropTypes.string,
    cta: PropTypes.string,
    image: ResponsiveImageProps.isRequired,
    showProgressTracker: PropTypes.bool,
  }).isRequired,
};

ConsultationHero.defaultProps = {
  content: contentDI,
  isReturning: false,
  routeParams: null,
  userFirstName: '',
};

export default ConsultationHero;
